import React, { useState, useEffect } from 'react';
import { Alert, Button, Space } from 'antd';
import { WifiOutlined, WifiOutlined as WifiOffOutlined, ReloadOutlined } from '@ant-design/icons';
import { getNetworkStatus } from '@/utils/request';

interface NetworkStatusProps {
  showOfflineOnly?: boolean; // 是否只在离线时显示
  position?: 'top' | 'bottom' | 'inline'; // 显示位置
  onNetworkChange?: (isOnline: boolean) => void; // 网络状态变化回调
}

const NetworkStatus: React.FC<NetworkStatusProps> = ({
  showOfflineOnly = false,
  position = 'top',
  onNetworkChange
}) => {
  const [isOnline, setIsOnline] = useState(getNetworkStatus());
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);

  useEffect(() => {
    const checkNetworkStatus = () => {
      const currentStatus = getNetworkStatus();
      setIsOnline(currentStatus);

      if (onNetworkChange) {
        onNetworkChange(currentStatus);
      }

      // 如果离线，显示离线提示
      if (!currentStatus) {
        setShowOfflineAlert(true);
      } else {
        setShowOfflineAlert(false);
      }
    };

    // 初始检查
    checkNetworkStatus();

    // 监听网络状态变化
    const handleOnline = () => {
      checkNetworkStatus();
    };

    const handleOffline = () => {
      checkNetworkStatus();
    };

    // 定期检查网络状态（每30秒）
    const interval = setInterval(checkNetworkStatus, 30000);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [onNetworkChange]);

  // 如果只在离线时显示且当前在线，则不渲染
  if (showOfflineOnly && isOnline) {
    return null;
  }

  // 如果离线，显示离线提示
  if (!isOnline) {
    const offlineAlert = (
      <Alert
        message="网络连接已断开"
        description="当前处于离线状态，部分功能可能无法正常使用。请检查网络设置后重试。"
        type="warning"
        showIcon
        icon={<WifiOffOutlined />}
        action={
          <Space>
            <Button
              size="small"
              type="link"
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
            >
              刷新页面
            </Button>
            <Button
              size="small"
              type="link"
              onClick={() => setShowOfflineAlert(false)}
            >
              关闭
            </Button>
          </Space>
        }
        style={{
          marginBottom: position === 'top' ? 16 : 0,
          marginTop: position === 'bottom' ? 16 : 0,
        }}
      />
    );

    if (position === 'inline') {
      return offlineAlert;
    }

    return (
      <div style={{
        position: position === 'top' ? 'sticky' : 'relative',
        top: position === 'top' ? 0 : 'auto',
        zIndex: 1000,
        backgroundColor: '#fff',
        borderBottom: position === 'top' ? '1px solid #f0f0f0' : 'none',
        padding: position === 'top' ? '8px 16px' : '0',
      }}>
        {offlineAlert}
      </div>
    );
  }

  // 如果在线且不只在离线时显示，显示在线状态（可选）
  if (!showOfflineOnly && position === 'inline') {
    return (
      <Alert
        message="网络连接正常"
        description="所有功能都可以正常使用。"
        type="success"
        showIcon
        icon={<WifiOutlined />}
        style={{ marginBottom: 16 }}
      />
    );
  }

  return null;
};

export default NetworkStatus;
